<template>
<div class="pl-drawer-overlay" @click.self="$emit('close')">
    <aside class="pl-drawer">
        <div class="pl-drawer-hd" :style="{ '--plwc': wc.colour(chip.workcentre_id, chip.type) }">
            <span class="pl-drawer-wcb">
                <svg class="pl-i"><use :href="'#' + wc.icon(chip.workcentre_id, chip.type)"/></svg>
                {{ wc.name(chip.workcentre_id) || typeLabel }}
            </span>
            <button type="button" class="pl-drawer-close" @click="$emit('close')">✕</button>
            <h3>{{ chip.pretty_name }}</h3>
            <div class="pl-drawer-qln">
                <template v-if="chip.quote_id">Q{{ chip.quote_id }} · {{ chip.quote_name }} · </template>
                est {{ fmtSecs(chip.planned_seconds) }}
                <span v-if="chip.is_draft" class="pl-pill pl-pill-draft">Draft</span>
                <span v-if="chip.checked" class="pl-drawer-done">✓ done</span>
            </div>
        </div>

        <div v-if="error" class="pl-drawer-error">{{ error }}</div>

        <div class="pl-drawer-sec" v-if="permissions.can_tick && (chip.checklist_id || isTyped)">
            <h4>Progress</h4>
            <button v-if="!chip.checked" type="button" class="pl-dbtn pl-dbtn-primary" :disabled="busy" @click="tick(true)">
                ✓ Mark complete
            </button>
            <button v-else type="button" class="pl-dbtn" :disabled="busy" @click="tick(false)">
                ↩ Undo tick
            </button>
            <div v-if="chip.checklist_id && permissions.can_manage" class="pl-dfield">
                <label>On behalf of</label>
                <select v-model="onBehalfOf" class="pl-dinput">
                    <option :value="null">Myself</option>
                    <option v-for="p in people" :key="p.id" :value="p.id">{{ p.name }}</option>
                </select>
            </div>
        </div>

        <div class="pl-drawer-sec" v-if="sheetUrl">
            <h4>Job</h4>
            <a class="pl-dbtn" :href="sheetUrl" data-ajax="false">📄 Production sheet</a>
        </div>

        <div class="pl-drawer-sec">
            <h4>Schedule</h4>
            <div class="pl-dfield">
                <label>Day</label>
                <input type="date" v-model="editDate" class="pl-dinput" :disabled="!movable">
            </div>
            <div class="pl-dfield">
                <label>Person</label>
                <select v-model="editUserId" class="pl-dinput" :disabled="!movable">
                    <option v-for="p in people" :key="p.id" :value="p.id">{{ p.name }}</option>
                </select>
            </div>
            <button v-if="movable && scheduleChanged" type="button" class="pl-dbtn pl-dbtn-dark" :disabled="busy" @click="reschedule(false)">
                Move to {{ editDate }}
            </button>
            <div class="pl-dnote" v-if="movable">
                Out-of-sequence or past dates are refused on the grid — this picker is the deliberate
                exception path: it warns, then logs who did it.
            </div>
            <div class="pl-dnote" v-else>
                {{ chip.checked ? 'Completed processes don\'t move.' : 'You don\'t have rights to move this chip.' }}
            </div>
        </div>

        <div class="pl-drawer-sec" v-if="chip.checklist_id && permissions.can_manage">
            <h4>Details</h4>
            <div class="pl-dfield">
                <label>Est minutes</label>
                <input type="number" min="0" max="10000" v-model.number="editMinutes" class="pl-dinput">
                <button v-if="minutesChanged" type="button" class="pl-dbtn-sm" :disabled="busy" @click="saveMinutes">Save</button>
            </div>
            <div class="pl-dfield">
                <label>People</label>
                <input type="number" min="1" max="9" v-model.number="editPeople" class="pl-dinput">
                <button v-if="peopleChanged" type="button" class="pl-dbtn-sm" :disabled="busy" @click="savePeople">Save</button>
            </div>
        </div>

        <div class="pl-drawer-sec" v-if="chip.checklist_id && permissions.can_tick && printerList.length">
            <h4>Labels</h4>
            <div class="pl-dfield">
                <label>Printer</label>
                <select v-model="printerKey" class="pl-dinput">
                    <option v-for="p in printerList" :key="p.printer_key" :value="p.printer_key">{{ p.name }}</option>
                </select>
            </div>
            <div class="pl-drow">
                <button type="button" class="pl-dbtn" :disabled="busy || !printerKey" @click="printLabel('tracking')">Tracking label</button>
                <button type="button" class="pl-dbtn" :disabled="busy || !printerKey" @click="printLabel('packing')">Packing label</button>
            </div>
            <div v-if="printed" class="pl-dprinted">✓ sent to printer</div>
        </div>

        <div class="pl-drawer-sec" v-if="chip.quote_id">
            <h4>Notes on Q{{ chip.quote_id }}</h4>
            <textarea v-model="noteBody" class="pl-dinput pl-dnote-input" rows="2" maxlength="2000"
                      placeholder="Add a note — anyone can, locked or not"></textarea>
            <button type="button" class="pl-dbtn" :disabled="busy || !noteBody.trim()" @click="addNote">＋ Add note</button>
            <div v-if="notes.length" class="pl-dnotes">
                <div v-for="n in notes" :key="n.id" class="pl-dnote-row">
                    <div class="pl-dnote-meta"><b>{{ n.user_name }}</b> · {{ n.created_at }}</div>
                    <div class="pl-dnote-body">{{ n.body }}</div>
                </div>
            </div>
        </div>

        <div class="pl-drawer-sec" v-if="movable">
            <h4>Slot</h4>
            <button type="button" class="pl-dbtn pl-dbtn-danger" :disabled="busy" @click="removeSlot">
                {{ isTyped ? 'Delete this entry' : 'Remove from the plan (back to unscheduled)' }}
            </button>
        </div>
    </aside>
</div>
</template>

<script>
import { fmtSecs, errMsg, wcLookups, productionSheetUrl } from './shared';

export default {
    name: 'ChipDrawer',

    props: {
        chip: { type: Object, required: true },
        board: { type: Object, required: true },
        permissions: { type: Object, required: true },
    },

    data() {
        return {
            editDate: this.chip.date,
            editUserId: this.chip.user_id,
            // The field edits the ROW's whole estimate — a multi-person or
            // multi-day process carries only its share in planned_seconds.
            editMinutes: Math.round((this.chip.est_seconds != null ? this.chip.est_seconds : this.chip.planned_seconds) / 60),
            editPeople: this.chip.num_people || 1,
            onBehalfOf: null,
            busy: false,
            error: null,
            printers: {},
            printerKey: null,
            printed: false,
            notes: [],
            noteBody: '',
        };
    },

    created() {
        // DYMO printers registered as online by the /printers page.
        window.axios.get('/printers/status')
            .then(res => {
                this.printers = res.data || {};
                const first = this.printerList[0];
                if (first) this.printerKey = first.printer_key;
            })
            .catch(() => { this.printers = {}; });

        if (this.chip.quote_id) {
            window.axios.get('/quote/' + this.chip.quote_id + '/notes')
                .then(res => { this.notes = res.data.notes || []; })
                .catch(() => { this.notes = []; });
        }
    },

    computed: {
        wc() {
            return wcLookups(this.board.workcentres);
        },
        people() {
            return this.board.people;
        },
        movable() {
            if (this.chip.checked) return false;
            return this.chip.released ? this.permissions.can_replan : this.permissions.can_place;
        },
        scheduleChanged() {
            return this.editDate !== this.chip.date || this.editUserId !== this.chip.user_id;
        },
        minutesChanged() {
            const current = this.chip.est_seconds != null ? this.chip.est_seconds : this.chip.planned_seconds;
            return this.editMinutes !== Math.round(current / 60);
        },
        peopleChanged() {
            return this.editPeople !== (this.chip.num_people || 1);
        },
        typeLabel() {
            return this.chip.type === 'delivery' ? 'Delivery' : this.chip.type === 'adhoc' ? 'Ad-hoc' : 'Misc';
        },
        isTyped() {
            return this.chip.type !== 'process';
        },
        printerList() {
            return Object.values(this.printers || {}).filter(p => p && p.printer_key);
        },
        sheetUrl() {
            return this.chip.item_id ? productionSheetUrl(this.chip.item_id) : null;
        },
    },

    methods: {
        fmtSecs,

        tick(checked) {
            this.busy = true;
            this.error = null;
            // Ad-hoc / delivery entries have no checklist row — their
            // completion lives on the slot itself.
            const req = this.chip.checklist_id
                ? window.axios.put('/checklist', {
                    type: 'process',
                    id: this.chip.checklist_id,
                    checked: checked,
                    user_id: this.onBehalfOf,
                })
                : window.axios.post('/planner/slot/' + this.chip.id + '/tick', { on: checked });
            req
                .then(() => { this.$emit('changed'); this.$emit('close'); })
                .catch(err => { this.error = errMsg(err); })
                .then(() => { this.busy = false; });
        },

        reschedule(allowOutOfSequence) {
            this.busy = true;
            this.error = null;
            window.axios.put('/planner/slot/' + this.chip.id, {
                user_id: this.editUserId,
                scheduled_date: this.editDate,
                allow_out_of_sequence: allowOutOfSequence,
            })
                .then(() => { this.$emit('changed'); this.$emit('close'); })
                .catch(err => {
                    const msg = errMsg(err);
                    if (!allowOutOfSequence && /out of sequence/i.test(msg)
                        && window.confirm(msg + '\n\nPlace it anyway? This is logged as a deliberate exception.')) {
                        this.reschedule(true);
                        return;
                    }
                    this.error = msg;
                })
                .then(() => { this.busy = false; });
        },

        saveMinutes() {
            this.busy = true;
            this.error = null;
            window.axios.put('/checklist/est-seconds', {
                id: this.chip.checklist_id,
                minutes: this.editMinutes,
            })
                .then(() => { this.$emit('changed'); })
                .catch(err => { this.error = errMsg(err); })
                .then(() => { this.busy = false; });
        },

        savePeople() {
            this.busy = true;
            this.error = null;
            window.axios.put('/checklist/num-people', {
                id: this.chip.checklist_id,
                num_people: this.editPeople,
            })
                .then(() => { this.$emit('changed'); })
                .catch(err => { this.error = errMsg(err); })
                .then(() => { this.busy = false; });
        },

        printLabel(type) {
            this.busy = true;
            this.printed = false;
            window.axios.post('/checklist/print-label', {
                type: type,
                id: this.chip.checklist_id,
                printer_key: this.printerKey,
                quantity: 1,
            })
                .then(() => { this.printed = true; })
                .catch(err => { this.error = errMsg(err); })
                .then(() => { this.busy = false; });
        },

        addNote() {
            if (!this.noteBody.trim()) return;
            this.busy = true;
            window.axios.post('/quote/' + this.chip.quote_id + '/notes', { body: this.noteBody.trim() })
                .then(res => {
                    this.notes.unshift(res.data.note);
                    this.noteBody = '';
                })
                .catch(err => { this.error = errMsg(err); })
                .then(() => { this.busy = false; });
        },

        removeSlot() {
            const msg = this.isTyped
                ? 'Delete this entry? It is not attached to a job and will be gone for good.'
                : 'Remove this slot? The process goes back to the unscheduled tray.';
            if (!window.confirm(msg)) return;
            this.busy = true;
            window.axios.delete('/planner/slot/' + this.chip.id)
                .then(() => { this.$emit('changed'); this.$emit('close'); })
                .catch(err => { this.error = errMsg(err); this.busy = false; });
        },
    },
};
</script>

<style scoped>
.pl-drawer-overlay{position:fixed; inset:0; background:rgba(11,11,11,.28); z-index:80;
    display:flex; justify-content:flex-end}
.pl-drawer{width:380px; max-width:94vw; height:100%; overflow-y:auto;
    background:#fcfcfb; box-shadow:-8px 0 30px rgba(11,11,11,.2);
    font:15px/1.5 system-ui,-apple-system,"Segoe UI",sans-serif; color:#0b0b0b}
.pl-drawer-hd{padding:16px 18px 13px; border-bottom:1px solid #e1e0d9; background:#f4f3f0; position:relative}
.pl-drawer-hd::before{content:""; position:absolute; left:0; top:0; bottom:0; width:6px; background:var(--plwc, #898781)}
.pl-drawer-wcb{display:inline-flex; align-items:center; gap:6px; font-size:11.5px; font-weight:800;
    color:var(--plwc, #898781); letter-spacing:.03em; text-transform:uppercase}
.pl-drawer-wcb .pl-i{width:13px; height:13px}
.pl-drawer-hd h3{font-size:17px; font-weight:800; margin:4px 0 2px}
.pl-drawer-qln{font-size:13px; color:#52514e; font-weight:600; display:flex; align-items:center; gap:7px; flex-wrap:wrap}
.pl-drawer-done{color:#0ca30c; font-weight:800; font-size:12px}
.pl-drawer-close{position:absolute; right:12px; top:12px; border:1px solid #e1e0d9; background:#fcfcfb;
    width:32px; height:32px; border-radius:8px; font-weight:800; color:#52514e; cursor:pointer}
.pl-drawer-error{margin:12px 16px 0; background:#f9e4e4; border:1px solid #d03b3b; color:#7c1d1d;
    border-radius:8px; padding:8px 11px; font-size:13px; font-weight:600}
.pl-drawer-sec{padding:13px 18px; border-bottom:1px solid #e1e0d9}
.pl-drawer-sec h4{font-size:11.5px; letter-spacing:.06em; text-transform:uppercase; color:#898781; font-weight:800; margin:0 0 9px}
.pl-dbtn{display:flex; align-items:center; justify-content:center; gap:9px; width:100%;
    border:1px solid #e1e0d9; background:#f9f9f7; border-radius:10px;
    padding:11px 12px; font:inherit; font-weight:700; font-size:14px; color:#0b0b0b; margin-bottom:7px; cursor:pointer}
.pl-dbtn:disabled{opacity:.55; cursor:default}
a.pl-dbtn{text-decoration:none}
.pl-dbtn-primary{background:#0ca30c; border-color:#0ca30c; color:#fff}
.pl-dbtn-dark{background:#0b0b0b; border-color:#0b0b0b; color:#fff}
.pl-dbtn-danger{background:#fcfcfb; border-color:#d03b3b; color:#d03b3b}
.pl-dbtn-sm{border:1px solid #0b0b0b; background:#0b0b0b; color:#fff; border-radius:8px;
    padding:8px 12px; font:inherit; font-weight:700; font-size:13px; cursor:pointer}
.pl-dfield{display:flex; align-items:center; gap:9px; margin-bottom:8px}
.pl-dfield label{font-size:13px; font-weight:650; color:#52514e; width:92px; flex:none}
.pl-dinput{flex:1; border:1px solid #e1e0d9; border-radius:8px; padding:9px 11px; background:#fcfcfb;
    font:inherit; font-weight:650; font-size:14px; min-width:0}
.pl-dnote{font-size:12.5px; color:#52514e; background:#fdf3d9; border-radius:8px; padding:8px 10px; line-height:1.4; margin-top:4px}
.pl-drow{display:flex; gap:8px}
.pl-drow .pl-dbtn{flex:1}
.pl-dprinted{font-size:13px; font-weight:800; color:#0ca30c; text-align:center; margin-top:2px}
.pl-dnote-input{width:100%; resize:vertical; margin-bottom:7px}
.pl-dnotes{margin-top:10px; max-height:220px; overflow-y:auto}
.pl-dnote-row{border-top:1px solid #e1e0d9; padding:8px 0}
.pl-dnote-meta{font-size:12px; color:#52514e}
.pl-dnote-body{font-size:13.5px; white-space:pre-wrap; margin-top:2px}
.pl-pill{font-size:10px; font-weight:800; letter-spacing:.04em; border-radius:20px; padding:1px 7px}
.pl-pill-draft{background:#f4f3f0; color:#52514e; border:1px dashed #898781}
.pl-i{display:inline-block}
</style>
